/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: IVRsPage.tsx
 🗓️ Created: 2025-01-27
 🧩 Description:
    Main page for IVRs management with list view.
    Displays IVR menus for automated call routing.

 🔧 Notes:
    - Domain-scoped data
    - Supports pagination and search
    - Includes delete confirmation

 🔁 Last Updated: 2025-11-07 by GPT-5 Codex
────────────────────────────────────────────────────────────────*/

import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, Edit, Copy, Trash2 } from 'lucide-react';
import DataGridSimple from '../../components/ui/DataGridSimple';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import { ModulePageContainer } from '../../components/layouts';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { useNavigationStore } from '../../store/navigation';
import {
  deleteIvrById,
  navigateToIvrDuplicate,
} from './ivrsActions';
import { useIVRList } from './hooks/useIVRList';
import { IVR } from './types';

const ACTION_BUTTON_CLASSNAMES = {
  base: 'p-2 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60',
  edit: 'hover:bg-blue-100 dark:hover:bg-blue-900/30',
  duplicate: 'hover:bg-emerald-100 dark:hover:bg-emerald-900/30',
  delete: 'hover:bg-red-100 dark:hover:bg-red-900/30',
};

export default function IVRsPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showSuccess, showError } = useNotifications();
  const { setCurrentPageTitle } = useNavigationStore();
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; ivr: IVR | null }>({
    isOpen: false,
    ivr: null
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const pageTitle = useMemo(() => t('ivrs:title'), [t]);

  useEffect(() => {
    setCurrentPageTitle(null);
    return () => setCurrentPageTitle(null);
  }, [setCurrentPageTitle]);

  const {
    data,
    loading,
    columns: baseColumns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = useIVRList();

  // Add custom renders to columns
  const columns = useMemo(
    () =>
      baseColumns.map((column) => {
        if (column.key === 'context') {
          return {
            ...column,
            render: (_value: unknown, item: IVR) => (
              <span className="font-mono text-sm text-muted-foreground">
                {item?.context || '-'}
              </span>
            ),
          };
        }
        if (column.key === 'timeout') {
          return {
            ...column,
            render: (_value: unknown, item: IVR) => (
              <span className="text-sm">{item?.timeout || 5}</span>
            ),
          };
        }
        if (column.key === 'enabled') {
          return {
            ...column,
            render: (_value: unknown, item: IVR) => (
              <span
                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  item?.enabled
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                }`}
              >
                {item?.enabled ? t('common:enabled') : t('common:disabled')}
              </span>
            ),
          };
        }
        return column;
      }),
    [baseColumns, t],
  );

  const handleAdd = () => {
    navigate('/pbx/incoming-tools/ivr/new');
  };

  const handleEdit = (ivr: IVR) => {
    navigate(`/pbx/incoming-tools/ivr/${ivr.uuid ?? ivr.id}`);
  };

  const handleDuplicate = async (ivr: IVR) => {
    try {
      navigateToIvrDuplicate(navigate, ivr.uuid ?? ivr.id);
    } catch (error) {
      showError(error instanceof Error ? error.message : t('ivrs:messages.duplicateError'));
    }
  };

  const handleDeleteClick = (ivr: IVR) => {
    setDeleteModal({ isOpen: true, ivr });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.ivr) return;

    setIsDeleting(true);
    try {
      const idToUse = deleteModal.ivr.uuid ?? deleteModal.ivr.id;
      await deleteIvrById(idToUse);

      showSuccess(t('ivrs:messages.deleteSuccess'));
      setDeleteModal({ isOpen: false, ivr: null });
      refresh();
    } catch (error) {
      showError(error instanceof Error ? error.message : t('ivrs:messages.deleteError'));
    } finally {
      setIsDeleting(false);
    }
  };

  const renderRowActions = (item: IVR) => (
    <div className="flex items-center gap-2">
      <SimpleTooltip content={t('ivrs:actions.edit')} position="top">
        <button
          type="button"
          onClick={() => handleEdit(item)}
          aria-label={t('ivrs:actions.edit')}
          className={`${ACTION_BUTTON_CLASSNAMES.base} ${ACTION_BUTTON_CLASSNAMES.edit}`}
        >
          <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        </button>
      </SimpleTooltip>
      <SimpleTooltip content={t('ivrs:actions.duplicate')} position="top">
        <button
          type="button"
          onClick={() => handleDuplicate(item)}
          aria-label={t('ivrs:actions.duplicate')}
          className={`${ACTION_BUTTON_CLASSNAMES.base} ${ACTION_BUTTON_CLASSNAMES.duplicate}`}
        >
          <Copy className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
        </button>
      </SimpleTooltip>
      <SimpleTooltip content={t('ivrs:actions.delete')} position="top">
        <button
          type="button"
          onClick={() => handleDeleteClick(item)}
          aria-label={t('ivrs:actions.delete')}
          className={`${ACTION_BUTTON_CLASSNAMES.base} ${ACTION_BUTTON_CLASSNAMES.delete}`}
        >
          <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
        </button>
      </SimpleTooltip>
    </div>
  );

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <DataGridSimple
          data={data}
          columns={columns}
          loading={loading}
          pagination={pagination}
          searchQuery={searchQuery}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSearch={handleSearch}
          onSort={handleSort}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          onColumnToggle={handleColumnToggle}
          onAdd={handleAdd}
          onDuplicate={handleDuplicate}
          title={t('ivrs:title')}
          titleIcon={<Phone className="h-5 w-5 text-primary" />}
          addButtonText={t('ivrs:actions.add')}
          renderActions={renderRowActions}
          searchPlaceholder={t('ivrs:searchPlaceholder')}
          emptyMessage={t('ivrs:emptyMessage')}
        />
      }
      formView={null}
    >
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, ivr: null })}
        onConfirm={handleDeleteConfirm}
        title={t('ivrs:messages.deleteTitle')}
        message={t('ivrs:messages.confirmDelete', { name: deleteModal.ivr?.name || '' })}
        confirmText={t('ivrs:actions.delete')}
        cancelText={t('ivrs:actions.cancel')}
        type="danger"
        isLoading={isDeleting}
      />
    </ModulePageContainer>
  );
}

